import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, Clock, QrCode, Lock, AlertCircle, RefreshCw, X, ExternalLink } from 'lucide-react';

const Assinatura = ({ user, onLogout }) => {
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPrice, setSubscriptionPrice] = useState(49.90);
  const [loadingCard, setLoadingCard] = useState(false);
  const [loadingPix, setLoadingPix] = useState(false);
  const [showPixDialog, setShowPixDialog] = useState(false);
  const [pixData, setPixData] = useState(null);

  useEffect(() => {
    fetchSubscription();
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axiosInstance.get('/system-config');
      setSubscriptionPrice(res.data.subscription_price || 49.90);
    } catch (e) {
      console.error('Erro ao buscar config:', e);
    }
  };

  const fetchSubscription = async () => {
    try {
      const userId = user?.id || user?.user_id;
      const res = await axiosInstance.get(`/subscription/status/${userId}`);
      setSubscription(res.data);
    } catch (e) {
      console.error('Erro ao carregar assinatura:', e);
    }
  };

  // Redireciona para o checkout do Mercado Pago (assinatura recorrente)
  const handleSubscribeCard = async () => {
    setLoadingCard(true);
    try {
      const userId = user?.id || user?.user_id;
      const res = await axiosInstance.post('/subscription/create-checkout-link', {
        user_id: userId,
        email: user.email
      });
      // Abre o checkout do MP em nova aba
      window.open(res.data.checkout_url, '_blank');
      toast.info('Checkout aberto! Após assinar, volte aqui e atualize a página.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar link de assinatura');
    } finally {
      setLoadingCard(false);
    }
  };

  const handleSubscribePix = async () => {
    setLoadingPix(true);
    try {
      const userId = user?.id || user?.user_id;
      const res = await axiosInstance.post('/subscription/create-payment', {
        user_id: userId,
        email: user.email
      });
      setPixData(res.data);
      setShowPixDialog(true);
      toast.success('QR Code PIX gerado!');
    } catch (e) {
      toast.error('Erro ao gerar PIX');
    } finally {
      setLoadingPix(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Deseja realmente cancelar sua assinatura recorrente?')) return;
    try {
      const userId = user?.id || user?.user_id;
      await axiosInstance.post(`/subscription/cancel-preapproval/${userId}`);
      toast.success('Assinatura cancelada com sucesso');
      fetchSubscription();
    } catch (e) {
      toast.error('Erro ao cancelar assinatura');
    }
  };

  const getStatusInfo = () => {
    if (!subscription) return null;
    const map = {
      trial: { icon: <Clock className="w-5 h-5" />, label: 'Período Trial', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50', desc: `${subscription.days_remaining || 0} dias restantes de trial gratuito.` },
      active: { icon: <CheckCircle className="w-5 h-5" />, label: 'Assinatura Ativa', color: 'bg-green-500/20 text-green-300 border-green-500/50', desc: subscription.payment_method === 'card_recorrente' ? 'Assinatura recorrente ativa. Cobrança automática todo mês.' : 'Sua assinatura está ativa.' },
      expired: { icon: <AlertCircle className="w-5 h-5" />, label: 'Assinatura Expirada', color: 'bg-red-500/20 text-red-300 border-red-500/50', desc: 'Sua assinatura expirou. Renove para continuar.' },
      cancelled: { icon: <X className="w-5 h-5" />, label: 'Cancelada', color: 'bg-gray-500/20 text-gray-300 border-gray-500/50', desc: 'Sua assinatura foi cancelada.' },
      pending: { icon: <Clock className="w-5 h-5" />, label: 'Aguardando Pagamento', color: 'bg-blue-500/20 text-blue-300 border-blue-500/50', desc: 'Pagamento em processamento. Aguarde a confirmação.' },
    };
    return map[subscription.status] || { icon: <AlertCircle className="w-5 h-5" />, label: 'Sem Assinatura Ativa', color: 'bg-gray-500/20 text-gray-300 border-gray-500/50', desc: 'Escolha uma forma de pagamento abaixo para ativar.' };
  };

  const statusInfo = getStatusInfo();
  const canSubscribe = !subscription || !['active'].includes(subscription?.status);
  const isRecorrente = subscription?.payment_method === 'card_recorrente';

  return (
    <div className="flex min-h-screen" data-testid="assinatura-page">
      <Sidebar user={user} onLogout={onLogout} />

      <div className="flex-1 p-8 max-w-5xl">
        <SubscriptionCard user={user} />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" data-testid="assinatura-title">Assinatura</h1>
          <p className="text-gray-400">Gerencie sua assinatura e pagamentos</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status */}
          {statusInfo && subscription && (
            <Card className="glass border-white/10" data-testid="subscription-status-card">
              <CardHeader>
                <CardTitle className="text-white">Status da Assinatura</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${statusInfo.color.split(' ')[0]}`}>
                    {statusInfo.icon}
                  </div>
                  <div>
                    <Badge className={statusInfo.color} data-testid="subscription-status-badge">
                      {statusInfo.label}
                    </Badge>
                    <p className="text-sm text-gray-400 mt-1">{statusInfo.desc}</p>
                  </div>
                </div>

                {subscription.status === 'trial' && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-200">
                    Trial expira em <strong>{new Date(subscription.trial_end).toLocaleDateString('pt-BR')}</strong>
                  </div>
                )}

                {subscription.status === 'active' && subscription.subscription_end && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-sm text-green-200">
                    {isRecorrente ? 'Próxima cobrança' : 'Válido até'}:{' '}
                    <strong>{new Date(subscription.subscription_end).toLocaleDateString('pt-BR')}</strong>
                  </div>
                )}

                {subscription.status === 'active' && isRecorrente && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelSubscription}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 w-full"
                    data-testid="cancel-subscription-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancelar Assinatura Recorrente
                  </Button>
                )}

                {/* Botão atualizar após voltar do MP */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchSubscription}
                  className="text-gray-400 hover:text-white w-full"
                  data-testid="refresh-subscription-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-2" />
                  Atualizar status
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Plano */}
          <Card className="glass border-white/10" data-testid="plan-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Plano Mensal
              </CardTitle>
              <CardDescription className="text-gray-400">
                Acesso completo a todas as funcionalidades
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-4">
                <p className="text-5xl font-bold text-white mb-1">R$ {subscriptionPrice.toFixed(2).replace('.', ',')}</p>
                <p className="text-gray-400 text-sm">por mês</p>
              </div>

              <div className="space-y-2">
                {[
                  'Dashboard com métricas em tempo real',
                  'Gestão ilimitada de lançamentos',
                  'Análise financeira com IA',
                  'Cronograma e Gantt interativo',
                  'Gestão de obras e supervisores',
                  'Relatórios e exportações'
                ].map(f => (
                  <div key={f} className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                    <span className="text-sm">{f}</span>
                  </div>
                ))}
              </div>

              {canSubscribe && (
                <div className="pt-4 border-t border-white/10 space-y-3">
                  <p className="text-sm text-gray-400 font-medium">Formas de Pagamento:</p>

                  {/* Cartão recorrente — via checkout MP */}
                  <Button
                    onClick={handleSubscribeCard}
                    disabled={loadingCard}
                    data-testid="subscribe-card-button"
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 py-5"
                  >
                    {loadingCard ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        <div className="text-left">
                          <p className="font-semibold text-sm">Cartão de Crédito</p>
                          <p className="text-xs opacity-80">Assinatura recorrente • cobrança automática</p>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-70" />
                      </>
                    )}
                  </Button>

                  {/* PIX manual */}
                  <Button
                    onClick={handleSubscribePix}
                    disabled={loadingPix}
                    variant="outline"
                    data-testid="subscribe-pix-button"
                    className="w-full border-teal-500/40 text-teal-300 hover:bg-teal-500/10 py-5"
                  >
                    {loadingPix ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <>
                        <QrCode className="w-4 h-4 mr-2" />
                        <div className="text-left">
                          <p className="font-semibold text-sm">PIX</p>
                          <p className="text-xs opacity-70">Pagamento manual mensal</p>
                        </div>
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" /> Pagamento seguro via Mercado Pago
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info */}
        <Card className="glass border-white/10 mt-6" data-testid="additional-info-card">
          <CardHeader>
            <CardTitle className="text-white text-base">Como funciona</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-sm text-gray-300">
              <li className="flex items-start gap-3">
                <CreditCard className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                <span><strong>Cartão de Crédito:</strong> Você é redirecionado para o checkout seguro do Mercado Pago. Após cadastrar o cartão, a cobrança é automática todo mês.</span>
              </li>
              <li className="flex items-start gap-3">
                <QrCode className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" />
                <span><strong>PIX:</strong> Pagamento único via QR Code. Você precisa renovar manualmente todo mês.</span>
              </li>
              <li className="flex items-start gap-3">
                <Lock className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <span>Os dados do cartão são armazenados diretamente no Mercado Pago — nunca passam pelo nosso servidor.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                <span>Após confirmar o pagamento, clique em <strong>"Atualizar status"</strong> para refletir a mudança.</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Dialog PIX */}
        <Dialog open={showPixDialog} onOpenChange={setShowPixDialog}>
          <DialogContent className="glass border-white/10 max-w-md" data-testid="payment-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <QrCode className="w-5 h-5" /> Pagamento via PIX
              </DialogTitle>
            </DialogHeader>
            {pixData && (
              <div className="space-y-4">
                <p className="text-gray-400 text-center text-sm">Escaneie o QR Code para pagar</p>
                {pixData.qr_code_base64 && (
                  <img
                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                    alt="QR Code PIX"
                    data-testid="qr-code-image"
                    className="mx-auto bg-white p-4 rounded-lg w-48 h-48 object-contain"
                  />
                )}
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-gray-400 mb-2">Código PIX (Copiar e Colar)</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-black/30 p-2 rounded text-white break-all" data-testid="pix-code">
                      {pixData.qr_code?.substring(0, 60)}...
                    </code>
                    <Button
                      size="sm"
                      onClick={() => { navigator.clipboard.writeText(pixData.qr_code); toast.success('Código copiado!'); }}
                      data-testid="copy-pix-code-button"
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
                <p className="text-2xl font-bold text-white text-center">
                  R$ {pixData.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-200 text-center">
                  Após o pagamento, sua assinatura será ativada automaticamente em alguns minutos.
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Assinatura;
